import { Stack, useTheme } from "@mui/material";
import { Flag, MapPin } from "lucide-react";
import { useClientQuery } from "web_myplann/Api/Client/useClientQuery";
import type { ClientProfileAddressResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DataCard } from "web_myplann/Shared/Components/DataCard";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import { EmptyListSkeleton } from "web_myplann/Shared/Components/EmptyListSkeleton";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";

type AddressWithTypeId = ClientProfileAddressResponseDTO & {
	typeId: number;
};

export const AddressesPage = () => {
	const theme = useTheme();
	const { t } = useResource();

	const { data: userData } = useClientQuery();

	const addresses = userData?.addresses as AddressWithTypeId[] | undefined;

	const permanentAddress = addresses?.find((address) => address.typeId === 1);

	const contactAddresses = addresses?.filter((address) => address.typeId === 2);

	return (
		<MyPlannLayout
			disableContentPadding
			topBar={
				<NavigationHeader
					title={t(Resources.Addresses.title)}
					defaultRoute={Routing.Dashboard.path()}
				/>
			}
		>
			<Stack p={1.6}>
				{!permanentAddress &&
				(!contactAddresses || contactAddresses.length === 0) ? (
					<EmptyListSkeleton
						label={t(Resources.Profile.EmptyList.Addresses.label)}
						description={t(Resources.Profile.EmptyList.Addresses.description)}
					/>
				) : (
					<>
						<DataCard
							icon={<Flag size={16} color={theme.palette.grey[700]} />}
							label={t(Resources.Addresses.PermanentAddress.title)}
							noDivider={contactAddresses?.length === 0}
						>
							<DataRow
								label={t(Resources.Addresses.PermanentAddress.Street)}
								value={permanentAddress?.street}
							/>
							<DataRow
								label={t(Resources.Addresses.PermanentAddress.StreetNumber)}
								value={permanentAddress?.descriptiveNumber}
							/>
							<DataRow
								label={t(Resources.Addresses.PermanentAddress.City)}
								value={permanentAddress?.city}
							/>
							<DataRow
								label={t(Resources.Addresses.PermanentAddress.Zip)}
								value={permanentAddress?.zip}
							/>
							<DataRow
								label={t(Resources.Addresses.PermanentAddress.Country)}
								value={permanentAddress?.country}
							/>
						</DataCard>

						{contactAddresses?.map((address, addressIndex) => (
							<DataCard
								key={`${address.street}-${address.city}`}
								icon={<MapPin size={16} color={theme.palette.grey[700]} />}
								label={t(Resources.Addresses.ContactAddress.title)}
								noDivider={addressIndex === contactAddresses.length - 1}
							>
								<DataRow
									label={t(Resources.Addresses.ContactAddress.Street)}
									value={address.street}
								/>
								<DataRow
									label={t(Resources.Addresses.ContactAddress.StreetNumber)}
									value={address.descriptiveNumber}
								/>
								<DataRow
									label={t(Resources.Addresses.ContactAddress.City)}
									value={address.city}
								/>
								<DataRow
									label={t(Resources.Addresses.ContactAddress.Zip)}
									value={address.zip}
								/>
								<DataRow
									label={t(Resources.Addresses.ContactAddress.Country)}
									value={address.country}
								/>
							</DataCard>
						))}
					</>
				)}
			</Stack>
		</MyPlannLayout>
	);
};
